import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { rollText, calculateRunningScores, calculateMaxScore } from './bowlingScoring';
import { computePinDeckState } from './LiveScoreView';
import { QRCodeSVG } from 'qrcode.react';
import './LiveOverlayView.css';

// Transparent OBS browser-source overlay: /overlay/{slug}.
// Resolves the permanent per-user slug to the CURRENT live session by querying
// liveSessions (public read) where overlaySlug == slug, then renders a single
// dark-glass lower-third scoresheet card that updates in real time. When the
// bowler is not live (or nothing is scored yet) the page renders nothing, so
// the browser source is invisible. Display toggles ride on the live doc as
// overlayPrefs and apply mid-stream.

const APP_STORE_URL = 'https://apps.apple.com/us/app/advanced-bowling-tracker/id6759532374';

const DEFAULT_PREFS = {
  showDecks: true,
  showMax: true,
  showName: true,
  showAlley: false,
  showQR: true,
};

// Pin rows back-to-front: 7-8-9-10 / 4-5-6 / 2-3 / 1 (matches the iOS deck).
const DECK_ROWS = [[7, 8, 9, 10], [4, 5, 6], [2, 3], [1]];

function OverlayDeck({ standing, notapPins }) {
  return (
    <div className="obs-deck">
      {DECK_ROWS.map((row) => (
        <div className="obs-deck-row" key={row[0]}>
          {row.map((pin) => {
            let cls = 'obs-pin obs-pin-down';
            if (standing.includes(pin)) cls = 'obs-pin obs-pin-standing';
            if (notapPins.includes(pin)) cls = 'obs-pin obs-pin-notap';
            return <span className={cls} key={pin} />;
          })}
        </div>
      ))}
    </div>
  );
}

function OverlayFrameCell({ index, frames, isNoTap, running, showDeck, isCurrent }) {
  const frameNumber = index + 1;
  const frame = (frames || []).find((f) => f.number === frameNumber);
  const slots = frameNumber === 10 ? 3 : 2;
  const bowled = !!(frame && frame.rolls && frame.rolls.length);

  // Deck semantics match the iOS stills: first-ball leave for frames 1-9,
  // the most recent ball's rack state in the 10th.
  let deck = null;
  if (showDeck && bowled) {
    const deckRollIndex = frameNumber === 10 ? frame.rolls.length - 1 : 0;
    deck = computePinDeckState(frame, deckRollIndex, isNoTap);
  }

  const cumulative = index < running.length ? running[index] : null;

  return (
    <div className={`obs-cell${frameNumber === 10 ? ' obs-cell-tenth' : ''}${isCurrent ? ' obs-cell-current' : ''}`}>
      {showDeck ? (
        <div className="obs-deckrow">
          {deck ? <OverlayDeck standing={deck.standing} notapPins={deck.notapPins} /> : null}
        </div>
      ) : null}
      <div className="obs-rolls">
        {Array.from({ length: slots }, (_, r) => (
          <span className={`obs-roll${bowled ? '' : ' obs-roll-pending'}`} key={r}>
            {bowled ? rollText(frame, r, isNoTap) : ''}
          </span>
        ))}
      </div>
      <div className="obs-cum">{cumulative != null ? cumulative : ' '}</div>
    </div>
  );
}

export default function LiveOverlayView({ slug }) {
  const [session, setSession] = useState(null);

  // OBS respects page transparency; the site's global dark body background
  // must not paint behind the overlay card.
  useEffect(() => {
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return () => {
      document.documentElement.style.background = '';
      document.body.style.background = '';
    };
  }, []);

  useEffect(() => {
    if (!slug) return undefined;
    const q = query(collection(db, 'liveSessions'), where('overlaySlug', '==', slug));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const live = snap.docs
          .map((d) => d.data())
          .filter((d) => d.isActive && (!d.expiresAt || !d.expiresAt.toMillis || d.expiresAt.toMillis() > now))
          .sort((a, b) => ((b.updatedAt && b.updatedAt.toMillis) ? b.updatedAt.toMillis() : 0)
                        - ((a.updatedAt && a.updatedAt.toMillis) ? a.updatedAt.toMillis() : 0));
        setSession(live[0] || null);
      },
      () => setSession(null)
    );
    return unsubscribe;
  }, [slug]);

  if (!session) return null;

  const prefs = { ...DEFAULT_PREFS, ...(session.overlayPrefs || {}) };
  const games = session.games || [];
  const game = games.length ? games[games.length - 1] : null;
  if (!game || !(game.frames || []).length) return null;

  const frames = game.frames;
  const isNoTap = !!session.isNoTap;
  const running = calculateRunningScores(frames, isNoTap);
  const resolvedTotal = running.length ? running[running.length - 1] : 0;
  const maxPossible = game.isComplete ? null : calculateMaxScore(frames, isNoTap);
  const currentIndex = game.isComplete ? -1 : (session.currentFrameNumber || 1) - 1;

  const metaParts = [];
  if (session.leagueName) metaParts.push(session.leagueName);
  else if (session.tournamentName) metaParts.push(session.tournamentName);
  if (prefs.showAlley && session.alleyName) metaParts.push(session.alleyName);
  metaParts.push(`Game ${game.gameNumber || games.length}`);

  return (
    <div className="obs-overlay">
      <div className="obs-card">
        <div className="obs-head">
          {prefs.showName && session.bowlerName ? (
            <span className="obs-name">{session.bowlerName}</span>
          ) : null}
          <span className="obs-meta">{metaParts.join(' · ')}</span>
          <span className="obs-score">{resolvedTotal}</span>
          {prefs.showMax && maxPossible != null ? (
            <span className="obs-max">MAX {maxPossible}</span>
          ) : null}
        </div>
        <div className="obs-body">
          <div className="obs-strip">
            {Array.from({ length: 10 }, (_, i) => (
              <OverlayFrameCell
                key={i}
                index={i}
                frames={frames}
                isNoTap={isNoTap}
                running={running}
                showDeck={prefs.showDecks}
                isCurrent={i === currentIndex}
              />
            ))}
          </div>
          {prefs.showQR ? (
            <div className="obs-promo">
              <span className="obs-get">Get the app</span>
              <span className="obs-qr"><QRCodeSVG value={APP_STORE_URL} size={86} level="M" /></span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
